import React, { useState, useEffect, useRef } from 'react';
import { X, Plus, Skull, History, Save, Trash2 } from 'lucide-react';
import ChampionSelect from './ChampionSelect';
import { DEATH_TYPES } from '../constants';
import Modal from '../design-system/Modal';
import Button from '../design-system/Button';

const CHAMPION_POOL = {
  main: ['Caitlyn', "Kai'Sa", 'Lucian'],
  flex: ['Corki', 'Jhin'],
  extra: ['Ezreal', 'Tristana', 'Draven']
};

const MENTAL_STATES = ['Focus', 'Neutre', 'Tilté', 'Fatigué'];

// Clé localStorage pour le brouillon (seulement pour nouvelle game, pas pour edit)
const DRAFT_KEY = 'apex_game_form_draft';

const DEFAULT_FORM = {
  champion: 'Caitlyn',
  support: 'Nautilus',
  enemyAdc: 'Caitlyn',
  enemySupport: 'Nautilus',
  jungler: 'Graves',
  enemyJungler: 'Graves',
  result: 'W',
  duration: 28,
  kills: 0,
  deaths: 0,
  assists: 0,
  csPerMin: 8.5,
  damageDealt: 0,
  teamDamage: 0,
  deathDetails: [],
  keyMoments: '',
  feeling: 7,
  mental: 'Focus',
  patch: '', // V6.5 : optionnel, pré-rempli côté App.jsx selon le dernier patch vu
  isAutofill: false, // V6.6 : autofill flag
  role: 'ADC', // V6.6 : ADC / SUP / MID / TOP / JG
};

// Check si le form a été modifié par rapport au default
function isFormDirty(form, base) {
  const keys = Object.keys(base);
  for (const k of keys) {
    if (k === 'deathDetails') {
      if (form[k].length !== base[k].length) return true;
      continue;
    }
    if (form[k] !== base[k]) return true;
  }
  return false;
}

export default function GameFormModal({ onClose, onSave, gameCount, champions, version, existingGame = null, latestPatch = null, userSettings = null }) {
  const isEdit = !!existingGame;

  // Load draft from localStorage on first render (only for NEW games, not edits)
  const getInitialForm = () => {
    if (existingGame) return existingGame;
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Sanity check : vérifier qu'il a au moins les clés attendues
        if (parsed && typeof parsed === 'object' && 'champion' in parsed) {
          return parsed;
        }
      }
    } catch (e) {
      console.warn('Failed to load draft:', e);
    }
    // V6.5 : pré-remplir patch avec le dernier patch vu
    return { ...DEFAULT_FORM, patch: latestPatch || '' };
  };

  const [form, setForm] = useState(getInitialForm);
  // V6.8 : liste des champions mis de côté pour affichage barré dans le sélecteur
  const sidelinedChampions = (userSettings?.champion_pool || [])
    .filter(p => p.status === 'sidelined')
    .map(p => p.name);
  const [hasDraft, setHasDraft] = useState(!existingGame && !!localStorage.getItem(DRAFT_KEY));
  const [currentDeath, setCurrentDeath] = useState({ minute: '', type: 'position', context: '' });
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const isFirstRender = useRef(true);

  // Auto-save draft on form change (débounce implicite via React render)
  // Only for new games, not edits
  useEffect(() => {
    if (existingGame) return; // Jamais de draft en mode edit
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return; // Ne pas re-save le brouillon au chargement initial
    }
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(form));
      setHasDraft(true);
    } catch (e) {
      console.warn('Failed to save draft:', e);
    }
  }, [form, existingGame]);

  // Reset draft to default
  const clearDraft = () => {
    if (!confirm('Effacer le brouillon et repartir à zéro ?')) return;
    localStorage.removeItem(DRAFT_KEY);
    setForm(DEFAULT_FORM);
    setHasDraft(false);
  };

  // Handle close with confirmation if form is dirty
  const handleClose = () => {
    if (existingGame) {
      // En mode edit, on ferme sans condition (pas de draft à protéger)
      onClose();
      return;
    }
    if (isFormDirty(form, DEFAULT_FORM)) {
      const keep = confirm('Fermer le formulaire ?\n\nTes modifications seront sauvegardées en brouillon et restaurées à la prochaine ouverture.\n\nOK pour fermer, Annuler pour continuer la saisie.');
      if (!keep) return;
    }
    onClose();
  };

  const addDeath = () => {
    if (!currentDeath.minute) return;
    setForm({
      ...form,
      deathDetails: [...form.deathDetails, { ...currentDeath, id: Date.now() }],
      deaths: form.deathDetails.length + 1
    });
    setCurrentDeath({ minute: '', type: 'position', context: '' });
  };

  const removeDeath = (id) => {
    const newDeaths = form.deathDetails.filter(d => d.id !== id);
    setForm({ ...form, deathDetails: newDeaths, deaths: newDeaths.length });
  };

  const save = async () => {
    setSaving(true);
    try {
      await onSave({
        ...form,
        timestamp: form.timestamp || new Date().toISOString(),
        gameNumber: form.gameNumber || gameCount + 1
      });
      // Success : on vide le brouillon (uniquement pour nouvelles games)
      if (!existingGame) {
        try { localStorage.removeItem(DRAFT_KEY); } catch (e) { /* ignore */ }
      }
    } catch (e) {
      alert('Erreur : ' + e.message);
      setSaving(false);
    }
  };

  const editHistory = existingGame?.edit_history || [];

  return (
    <Modal open onClose={onClose} title='Game #{form.gameNumber || gameCount + 1}' subtitle='' size="xl">
      <div className="space-y-4">
          {/* Résultat */}
          <div>
            <div className="text-[10px] tracking-[0.25em] text-zinc-500 uppercase mb-2 font-semibold">Résultat</div>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setForm({ ...form, result: 'W' })} className={`p-4 border font-black text-xl tracking-wider transition ${form.result === 'W' ? 'bg-gradient-to-r from-cyan-400 to-cyan-300 text-black border-cyan-400' : 'bg-transparent border-zinc-800 text-zinc-500 hover:border-zinc-700'}`}>VICTOIRE</button>
              <button onClick={() => setForm({ ...form, result: 'L' })} className={`p-4 border font-black text-xl tracking-wider transition ${form.result === 'L' ? 'bg-gradient-to-r from-fuchsia-500 to-rose-500 text-black border-fuchsia-500' : 'bg-transparent border-zinc-800 text-zinc-500 hover:border-zinc-700'}`}>DÉFAITE</button>
            </div>
          </div>

          {/* Matchup */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <ChampionSelect
                label="Ton Champion (ADC)"
                value={form.champion}
                onChange={(v) => setForm({ ...form, champion: v })}
                champions={champions}
                version={version}
                accent="cyan"
                sidelined={sidelinedChampions}
              />
              <div className="flex gap-1 mt-2 flex-wrap">
                {CHAMPION_POOL.main.map(c => (
                  <button key={c} onClick={() => setForm({ ...form, champion: c })} className="text-[10px] px-2 py-0.5 border border-cyan-400/30 text-cyan-400 hover:bg-cyan-400/10 transition">
                    {c}
                  </button>
                ))}
                {CHAMPION_POOL.flex.map(c => (
                  <button key={c} onClick={() => setForm({ ...form, champion: c })} className="text-[10px] px-2 py-0.5 border border-zinc-700 text-zinc-400 hover:bg-zinc-800 transition">
                    {c}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <ChampionSelect
                label="Support Allié"
                value={form.support}
                onChange={(v) => setForm({ ...form, support: v })}
                champions={champions}
                version={version}
                accent="cyan"
              />
            </div>
            <div>
              <ChampionSelect
                label="ADC Ennemi"
                value={form.enemyAdc}
                onChange={(v) => setForm({ ...form, enemyAdc: v })}
                champions={champions}
                version={version}
                accent="magenta"
              />
            </div>
            <div>
              <ChampionSelect
                label="Support Ennemi"
                value={form.enemySupport}
                onChange={(v) => setForm({ ...form, enemySupport: v })}
                champions={champions}
                version={version}
                accent="magenta"
              />
            </div>
            <div>
              <ChampionSelect
                label="Jungler Allié"
                value={form.jungler}
                onChange={(v) => setForm({ ...form, jungler: v })}
                champions={champions}
                version={version}
                accent="cyan"
              />
            </div>
            <div>
              <ChampionSelect
                label="Jungler Ennemi"
                value={form.enemyJungler}
                onChange={(v) => setForm({ ...form, enemyJungler: v })}
                champions={champions}
                version={version}
                accent="magenta"
              />
            </div>
          </div>

          {/* Stats */}
          <div>
            <div className="text-[10px] tracking-[0.25em] text-zinc-500 uppercase mb-2 font-semibold">Stats (DPM.lol / OP.GG)</div>

            {/* V6.6 : Autofill flag + rôle (souvent oublié, donc visuellement saillant si activé) */}
            <div className={`mb-3 border p-3 transition ${form.isAutofill ? 'border-amber-400/50 bg-amber-400/5' : 'border-zinc-800 bg-zinc-950'}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={!!form.isAutofill}
                    onChange={(e) => setForm({ ...form, isAutofill: e.target.checked, role: e.target.checked ? (form.role === 'ADC' ? 'SUP' : form.role) : 'ADC' })}
                    className="w-4 h-4 accent-amber-400"
                  />
                  <span className={`text-xs font-bold tracking-wider ${form.isAutofill ? 'text-amber-400' : 'text-zinc-400'}`}>
                    AUTOFILL
                  </span>
                  <span className="text-[10px] text-zinc-500 font-mono">(exclu des analytics ADC par défaut)</span>
                </label>
                {form.isAutofill && (
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-zinc-500 font-mono">Rôle joué :</span>
                    <select
                      value={form.role}
                      onChange={(e) => setForm({ ...form, role: e.target.value })}
                      className="bg-zinc-900 border border-zinc-800 text-white text-xs p-1 font-mono focus:border-amber-400 focus:outline-none"
                    >
                      <option value="SUP">Support</option>
                      <option value="MID">Mid</option>
                      <option value="TOP">Top</option>
                      <option value="JG">Jungle</option>
                    </select>
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div><label className="text-[10px] text-zinc-600 font-mono">Durée (min)</label><input type="number" value={form.duration} onChange={(e) => setForm({ ...form, duration: +e.target.value })} className="w-full bg-zinc-900 border border-zinc-800 text-white p-2 font-mono mt-1 focus:border-cyan-400 focus:outline-none" /></div>
              <div>
                <label className="text-[10px] text-zinc-600 font-mono">Patch</label>
                <input type="text" value={form.patch || ''} onChange={(e) => setForm({ ...form, patch: e.target.value })} placeholder="ex: 26.09" className="w-full bg-zinc-900 border border-zinc-800 text-white p-2 font-mono mt-1 focus:border-violet-400 focus:outline-none" />
              </div>
              <div><label className="text-[10px] text-zinc-600 font-mono">Kills</label><input type="number" value={form.kills} onChange={(e) => setForm({ ...form, kills: +e.target.value })} className="w-full bg-zinc-900 border border-zinc-800 text-white p-2 font-mono mt-1 focus:border-cyan-400 focus:outline-none" /></div>
              <div><label className="text-[10px] text-zinc-600 font-mono">Assists</label><input type="number" value={form.assists} onChange={(e) => setForm({ ...form, assists: +e.target.value })} className="w-full bg-zinc-900 border border-zinc-800 text-white p-2 font-mono mt-1 focus:border-cyan-400 focus:outline-none" /></div>
              <div><label className="text-[10px] text-zinc-600 font-mono">CS/min</label><input type="number" step="0.1" value={form.csPerMin} onChange={(e) => setForm({ ...form, csPerMin: +e.target.value })} className="w-full bg-zinc-900 border border-zinc-800 text-white p-2 font-mono mt-1 focus:border-cyan-400 focus:outline-none" /></div>
              <div className="col-span-2"><label className="text-[10px] text-zinc-600 font-mono">Damage Perso</label><input type="number" value={form.damageDealt || 0} onChange={(e) => setForm({ ...form, damageDealt: +e.target.value })} placeholder="ex: 28500" className="w-full bg-zinc-900 border border-zinc-800 text-white p-2 font-mono mt-1 focus:border-cyan-400 focus:outline-none" /></div>
              <div className="col-span-2"><label className="text-[10px] text-zinc-600 font-mono">Damage Équipe Total</label><input type="number" value={form.teamDamage || 0} onChange={(e) => setForm({ ...form, teamDamage: +e.target.value })} placeholder="ex: 95000" className="w-full bg-zinc-900 border border-zinc-800 text-white p-2 font-mono mt-1 focus:border-fuchsia-400 focus:outline-none" /></div>

              {/* Métriques calculées */}
              <div className="col-span-2 border border-cyan-400/30 bg-cyan-400/5 p-2">
                <label className="text-[10px] text-cyan-400 font-mono tracking-wider">DMG / MIN</label>
                <div className="font-mono font-bold text-lg text-cyan-400">{form.duration > 0 && form.damageDealt > 0 ? Math.round(form.damageDealt / form.duration).toLocaleString() : '—'}</div>
              </div>
              <div className="col-span-2 border border-fuchsia-400/30 bg-fuchsia-400/5 p-2">
                <label className="text-[10px] text-fuchsia-400 font-mono tracking-wider">DAMAGE SHARE</label>
                <div className="font-mono font-bold text-lg text-fuchsia-400">{form.teamDamage > 0 && form.damageDealt > 0 ? (form.damageDealt / form.teamDamage * 100).toFixed(1) + '%' : '—'}</div>
              </div>

              <div className="col-span-4"><label className="text-[10px] text-zinc-600 font-mono">KDA</label><div className="w-full bg-zinc-900 border border-zinc-800 text-cyan-400 p-2 font-mono font-bold mt-1">{form.kills}/{form.deaths}/{form.assists} ({form.deaths === 0 ? 'PERFECT' : ((form.kills + form.assists) / form.deaths).toFixed(2)})</div></div>
            </div>
          </div>

          {/* Lane Comparison (si import Riot) */}
          {form.opponentStats && (
            <div className="border border-fuchsia-400/30 bg-fuchsia-400/5 p-4">
              <div className="text-[10px] tracking-[0.25em] text-fuchsia-400 uppercase mb-3 font-semibold flex items-center gap-2">
                ⚔️ Lane Comparison · Toi vs {form.opponentStats.champion}
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs font-mono">
                {[
                  { label: 'CS/min', mine: form.csPerMin, them: form.opponentStats.csPerMin, higher: true },
                  { label: 'Kills', mine: form.kills, them: form.opponentStats.kills, higher: true },
                  { label: 'Deaths', mine: form.deaths, them: form.opponentStats.deaths, higher: false },
                  { label: 'Damage', mine: form.damageDealt, them: form.opponentStats.damageDealt, higher: true, isLargeNum: true },
                  { label: 'DMG/min', mine: form.duration > 0 ? Math.round(form.damageDealt / form.duration) : 0, them: form.opponentStats.damagePerMin, higher: true, isLargeNum: true },
                  { label: 'Gold', mine: form.myStats?.goldEarned || 0, them: form.opponentStats.goldEarned, higher: true, isLargeNum: true },
                ].map(stat => {
                  const diff = stat.mine - stat.them;
                  const winning = stat.higher ? diff > 0 : diff < 0;
                  return (
                    <div key={stat.label} className="flex items-center justify-between gap-2 bg-zinc-950 px-2 py-1.5">
                      <span className="text-zinc-500 w-16">{stat.label}</span>
                      <div className="flex items-center gap-2 flex-1 justify-end">
                        <span className={winning ? 'text-cyan-400 font-bold' : 'text-zinc-400'}>{stat.isLargeNum ? stat.mine.toLocaleString() : stat.mine}</span>
                        <span className="text-zinc-600">vs</span>
                        <span className={!winning && diff !== 0 ? 'text-fuchsia-400 font-bold' : 'text-zinc-400'}>{stat.isLargeNum ? stat.them.toLocaleString() : stat.them}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-3 pt-3 border-t border-zinc-800 flex items-center justify-between text-xs font-mono">
                <span className="text-zinc-500">Kills directs sur lui</span>
                <span className="text-cyan-400 font-bold">{form.opponentStats.killsOfMe || 0}</span>
              </div>
              <div className="flex items-center justify-between text-xs font-mono">
                <span className="text-zinc-500">Morts à cause de lui</span>
                <span className="text-rose-400 font-bold">{form.opponentStats.killsOnMe || 0}</span>
              </div>
            </div>
          )}

          {/* Morts */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] tracking-[0.25em] text-zinc-500 uppercase font-semibold"><Skull size={12} className="inline mr-1" /> Morts Catégorisées</div>
              <div className="text-xs text-zinc-500 font-mono">{form.deathDetails.length} mort{form.deathDetails.length > 1 ? 's' : ''}</div>
            </div>

            {form.deathDetails.length > 0 && (
              <div className="space-y-2 mb-3">
                {form.deathDetails.map((d, idx) => {
                  const updateDeath = (updates) => {
                    const newDeaths = [...form.deathDetails];
                    newDeaths[idx] = { ...newDeaths[idx], ...updates, _categorized: true };
                    setForm({ ...form, deathDetails: newDeaths });
                  };
                  return (
                    <div key={d.id} className={`grid grid-cols-12 gap-2 items-center bg-zinc-900 border ${d._auto && !d._categorized ? 'border-amber-400/30' : 'border-zinc-800'} p-2`}>
                      <input
                        type="number"
                        value={d.minute}
                        onChange={(e) => updateDeath({ minute: e.target.value })}
                        className="col-span-1 bg-zinc-950 border border-zinc-800 text-white text-xs font-mono p-1 focus:border-cyan-400 focus:outline-none w-full"
                        title="Minute"
                      />
                      <select
                        value={d.type}
                        onChange={(e) => updateDeath({ type: e.target.value })}
                        className={`col-span-2 bg-zinc-950 border text-xs font-mono p-1 focus:outline-none w-full font-semibold tracking-wider uppercase ${DEATH_TYPES[d.type].textClass} ${DEATH_TYPES[d.type].borderClass}`}
                        title="Type de mort"
                      >
                        {Object.entries(DEATH_TYPES).map(([k, v]) => (
                          <option key={k} value={k} className="text-white bg-zinc-900">{v.label}</option>
                        ))}
                      </select>
                      <input
                        type="text"
                        value={d.context}
                        onChange={(e) => updateDeath({ context: e.target.value })}
                        className="col-span-8 bg-zinc-950 border border-zinc-800 text-zinc-300 text-xs p-1 focus:border-cyan-400 focus:outline-none w-full"
                        placeholder="Contexte de la mort..."
                      />
                      <button
                        onClick={() => removeDeath(d.id)}
                        className="col-span-1 text-zinc-600 hover:text-rose-400 flex justify-center"
                      >
                        <X size={14} />
                      </button>
                      {d._auto && !d._categorized && (
                        <div className="col-span-12 text-[9px] text-amber-400 font-mono tracking-wider px-1">
                          ⚠ AUTO-IMPORT — type suggéré, à valider
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <div className="grid grid-cols-12 gap-2">
              <input type="number" placeholder="Min" value={currentDeath.minute} onChange={(e) => setCurrentDeath({ ...currentDeath, minute: e.target.value })} className="col-span-2 bg-zinc-900 border border-zinc-800 text-white p-2 font-mono text-sm focus:border-cyan-400 focus:outline-none" />
              <select value={currentDeath.type} onChange={(e) => setCurrentDeath({ ...currentDeath, type: e.target.value })} className="col-span-3 bg-zinc-900 border border-zinc-800 text-white p-2 font-mono text-sm focus:border-cyan-400 focus:outline-none">
                {Object.entries(DEATH_TYPES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
              <input type="text" placeholder="Contexte" value={currentDeath.context} onChange={(e) => setCurrentDeath({ ...currentDeath, context: e.target.value })} onKeyDown={(e) => e.key === 'Enter' && addDeath()} className="col-span-5 bg-zinc-900 border border-zinc-800 text-white p-2 text-sm focus:border-cyan-400 focus:outline-none" />
              <button onClick={addDeath} className="col-span-2 bg-gradient-to-r from-cyan-400 to-fuchsia-400 text-black p-2 hover:opacity-90 transition text-sm font-bold tracking-wider"><Plus size={14} className="inline" /> ADD</button>
            </div>

            <div className="mt-2 text-[10px] text-zinc-600 font-mono grid grid-cols-3 gap-2">
              {Object.entries(DEATH_TYPES).map(([k, v]) => (
                <div key={k} className={`border-l-2 ${v.borderLeftClass} pl-2`}>
                  <span className={`${v.textClass} font-semibold`}>{v.label}</span>: {v.desc}
                </div>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <div className="text-[10px] tracking-[0.25em] text-zinc-500 uppercase mb-2 font-semibold">Moments Clés / Notes</div>
            <textarea value={form.keyMoments} onChange={(e) => setForm({ ...form, keyMoments: e.target.value })} placeholder="Ex: Bon roam mid à 12min, greedy sur dragon à 18min..." className="w-full bg-zinc-900 border border-zinc-800 text-white p-3 min-h-[80px] focus:border-cyan-400 focus:outline-none text-sm" />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-zinc-800">
            <button onClick={handleClose} disabled={saving} className="flex-1 border border-zinc-800 text-zinc-400 p-3 hover:border-zinc-700 transition font-semibold tracking-wider text-sm disabled:opacity-50">ANNULER</button>
            <button onClick={save} disabled={saving} className="flex-[2] bg-gradient-to-r from-cyan-400 to-fuchsia-400 text-black p-3 hover:opacity-90 transition font-black tracking-wider disabled:opacity-50">
              {saving ? 'SAUVEGARDE...' : (isEdit ? 'METTRE À JOUR' : 'SAUVEGARDER LA GAME')}
            </button>
          </div>
        </div>
    </Modal>
  );
}
